// Upload button for enrollment site assets (logos, PDFs). Presigns a PUT into the
// group-enrollment bucket's uploads/ prefix via the API, uploads directly from the
// browser, and hands back the public URL (served by the enroll site's CloudFront)
// for the caller to store in the site config.
import {useRef, useState} from 'react';
import {Loader2, Upload} from 'lucide-react';
import {trpc} from '@common-web/lib/trpc';
import {Button} from '@common-web/components/ui/button';
import {useToast} from '@common-web/hooks/use-toast';

// Generous client-side caps — large enough that real logos/PDFs never hit them, small
// enough to catch an accidental huge file. Not server-enforced (internal uploaders only).
export const MAX_LOGO_BYTES = 1024 * 1024; // 1 MB
export const MAX_PDF_BYTES = 10 * 1024 * 1024; // 10 MB

export function AssetUploadButton({
  groupSlug,
  kind,
  accept,
  maxBytes,
  disabled,
  label = 'Upload',
  onUploaded,
}: {
  groupSlug: string;
  kind: 'logo' | 'pdf';
  /** `accept` attribute for the file input, e.g. 'application/pdf'. */
  accept: string;
  /** Optional client-side size guard; oversized files are rejected with a toast. */
  maxBytes?: number;
  disabled?: boolean;
  label?: string;
  onUploaded: (publicUrl: string) => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const {toast} = useToast();
  const presignMutation = trpc.internal.enrollmentSite.createAssetUploadUrl.useMutation();

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    if (maxBytes !== undefined && file.size > maxBytes) {
      toast({
        variant: 'destructive',
        title: 'File too large',
        description: `Keep it under ${Math.round(maxBytes / 1024 / 1024)}MB.`,
      });
      return;
    }
    setUploading(true);
    try {
      const {uploadUrl, publicUrl} = await presignMutation.mutateAsync({
        groupSlug,
        kind,
        fileName: file.name,
        contentType: file.type,
      });
      const response = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {'Content-Type': file.type},
      });
      if (!response.ok) throw new Error(`Upload failed (${response.status}).`);
      onUploaded(publicUrl);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Upload failed',
        description: error instanceof Error ? error.message : 'Something went wrong.',
      });
    } finally {
      setUploading(false);
      // Allow re-selecting the same file after a failure or replacement.
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type='file'
        accept={accept}
        className='hidden'
        onChange={(e) => void handleFile(e.target.files?.[0])}
      />
      <Button
        size='sm'
        variant='outline'
        disabled={disabled || uploading}
        onClick={() => fileInputRef.current?.click()}
      >
        {uploading ? (
          <Loader2 className='h-3.5 w-3.5 mr-1.5 animate-spin' />
        ) : (
          <Upload className='h-3.5 w-3.5 mr-1.5' />
        )}
        {label}
      </Button>
    </>
  );
}
